import type {
  ChartRule,
  ChartRuleColumn,
  CustomChartDefinition,
  CustomChartSize,
  CustomChartType,
  DashboardPreset,
} from '@/entities/dashboards';
import { RECOMMENDED_CHART_SIZES } from '@/entities/dashboards';

/**
 * Диаграмма правящегося дашборда.
 *
 * Кроме полей самой диаграммы состав держит истолкованное правило и описание, по которому оно
 * получено: по их расхождению видно, что описание правили и правило нужно перестроить. Своё имя
 * поля идентификатора (`chartId`) — чтобы не путать его с `id` готовой диаграммы дашборда.
 */
export type PresetChartFormValues = {
  /** Идентификатор диаграммы: заводится при добавлении и правки переживает */
  chartId: string;
  title: string;
  chartType: CustomChartType;
  size: CustomChartSize;
  /** Высота, заданная растягиванием на сетке; `null` — высота подбирается под содержимое */
  height: number | null;
  /** Диаграмма начинает собой строку сетки, даже когда в предыдущей осталась незанятая ширина */
  startsRow: boolean;
  /** Описание нужных данных на естественном языке */
  description: string;
  /** Правило, закреплённое за описанием; `null` — описание ещё не истолковано */
  rule: ChartRule | null;
  /** Отметка бэкенда о проверенном запросе правила */
  signature: string;
  /** Описание, по которому получено правило; разошлось с `description` — правило устарело */
  resolvedDescription: string;
  /**
   * Тип диаграммы, для которого правило уже проверено. Разошёлся с `chartType` — тип сменили
   * после истолкования, и соответствие правила новому типу ещё предстоит проверить.
   */
  resolvedChartType: CustomChartType;
  /** Пересказ правила на русском — показывается пользователю на подтверждение */
  summary: string;
  /**
   * Значения выборки правила — из них задаются подстановки текста результата. Пусто, пока
   * правило в этот заход не исполнялось: тогда перечень берётся из наполнения диаграммы.
   */
  columns: ChartRuleColumn[];
};

/**
 * Правящееся правило диаграммы: сам запрос и текст его результата.
 *
 * Держится отдельно от значений формы: правилами формы запрос и текст не проверяются, а
 * сохраняются иначе, чем описание, — заверением либо вовсе без обращения к бэкенду.
 */
export type ChartRuleDraft = {
  query: string;
  template: string;
};

/** Правящийся состав пользовательского дашборда: его название и диаграммы. */
export type DashboardPresetFormValues = {
  name: string;
  charts: PresetChartFormValues[];
};

/** Тип диаграммы, предлагаемый новой диаграмме: самый простой из каталога. */
const DEFAULT_CHART_TYPE: CustomChartType = 'metric';

/** Размер новой диаграммы — первый рекомендуемый для её типа. */
export const defaultSizeForType = (chartType: CustomChartType): CustomChartSize =>
  RECOMMENDED_CHART_SIZES[chartType][0];

/**
 * Пустая диаграмма для добавления в состав дашборда: название, тип и размер ей даст
 * истолкование описания, а до него заготовка держит их значениями каталога. Начало строки
 * приходит от места, откуда диаграмму заводят: место в конце сетки ставит её следующей строкой.
 */
export const createEmptyChart = (startsRow: boolean): PresetChartFormValues => ({
  chartId: crypto.randomUUID(),
  title: '',
  chartType: DEFAULT_CHART_TYPE,
  size: defaultSizeForType(DEFAULT_CHART_TYPE),
  height: null,
  startsRow,
  description: '',
  rule: null,
  signature: '',
  resolvedDescription: '',
  resolvedChartType: DEFAULT_CHART_TYPE,
  summary: '',
  columns: [],
});

/**
 * Раскладывает пресет в правящийся состав. Правило считается действующим для сохранённого
 * описания — правка описания сама сделает его устаревшим. Нового дашборда правка начинается
 * с пустой сетки: диаграммы пользователь добавляет на ней сам.
 */
export const toFormValues = (preset: DashboardPreset | null): DashboardPresetFormValues => {
  if (!preset) return { name: '', charts: [] };
  return {
    name: preset.name,
    charts: preset.charts.map((chart) => ({
      chartId: chart.id,
      title: chart.title,
      chartType: chart.chartType,
      size: chart.size,
      height: chart.height ?? null,
      startsRow: chart.startsRow ?? false,
      description: chart.description,
      rule: chart.rule,
      // Пресет приходит из настроек пользователя и мог быть записан прежним составом
      // диаграммы: у записанной до заверения диаграммы отметки нет.
      signature: chart.signature ?? '',
      resolvedDescription: chart.description,
      resolvedChartType: chart.chartType,
      summary: '',
      // Правило сохранённой диаграммы через истолкование не проходит — значения её выборки
      // становятся известны с первого наполнения.
      columns: [],
    })),
  };
};

/** Диаграмма правящегося состава с уже закреплённым правилом — её и показывает дашборд. */
export type ResolvedPresetChart = PresetChartFormValues & { rule: ChartRule };

/** Правило за диаграммой закреплено — её есть чем наполнить. */
export const isResolvedChart = (chart: PresetChartFormValues): chart is ResolvedPresetChart => chart.rule !== null;

/** Собирает диаграмму дашборда из её правящихся значений. */
export const toChartDefinition = (chart: ResolvedPresetChart): CustomChartDefinition => ({
  id: chart.chartId,
  title: chart.title.trim(),
  chartType: chart.chartType,
  size: chart.size,
  ...(chart.height === null ? {} : { height: chart.height }),
  ...(chart.startsRow ? { startsRow: true } : {}),
  description: chart.description.trim(),
  rule: chart.rule,
  signature: chart.signature,
});

/**
 * Собирает состав дашборда из правящихся значений. Диаграммы без правила в состав не попадают:
 * описание каждой истолковано ещё в её свойствах.
 */
export const toChartDefinitions = (charts: PresetChartFormValues[]): CustomChartDefinition[] =>
  charts.filter(isResolvedChart).map(toChartDefinition);
